import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import {
    FaCheck, FaTimes, FaFileInvoiceDollar, FaChartLine,
    FaPlus, FaArrowRight, FaSearch, FaTrash, FaFilter, FaHistory,
    FaUserMd, FaWallet, FaHandHoldingUsd, FaFileInvoice, FaArrowLeft, FaBell
} from 'react-icons/fa';
import InvoiceGenerator from '../components/InvoiceGenerator';
import AccountStatement from '../components/AccountStatement';
import PeriodicInvoiceGenerator from '../components/PeriodicInvoiceGenerator';
import RevenueAnalytics from '../components/RevenueAnalytics';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/Accounting.css';

const Accounting = () => {
    const { invoices, updateInvoice, deleteInvoice, orders, clients, payments, addPayment, deletePayment, creditNotes, updateCreditNote, deleteCreditNote, getClientAccountingSummary, products } = useData();
    const navigate = useNavigate();
    const location = useLocation();

    // Support query param ?tab=analytics or ?tab=stats
    const queryTab = new URLSearchParams(location.search).get('tab');
    const initialTab = queryTab === 'analytics' || queryTab === 'stats' ? 'analytics' : 'invoices';
    const [activeTab, setActiveTab] = useState(initialTab); // invoices, statements, analytics
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showBillable, setShowBillable] = useState(false);
    const [selectedClientForStatement, setSelectedClientForStatement] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    useEffect(() => {
        const tab = new URLSearchParams(location.search).get('tab');
        if (tab === 'analytics' || tab === 'stats') {
            setActiveTab('analytics');
        }
    }, [location.search]);

    // Reset to first page when search, filter or itemsPerPage changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filter, itemsPerPage]);

    // Calculations
    const totalBilled = invoices.reduce((acc, inv) => acc + (parseFloat(inv.amount || inv.total_amount) || 0), 0);
    const totalPayments = payments.reduce((sum, p) => {
        const amt = parseFloat(p.amount || 0);
        const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
        return sum + (isRefund ? -amt : amt);
    }, 0);
    const totalCredits = creditNotes
        .filter(n => n.status !== 'pending')
        .reduce((sum, n) => sum + parseFloat(n.amount || 0), 0);
    
    const totalPaid = totalPayments + totalCredits;
    const totalPending = Math.max(0, totalBilled - totalPaid);

    // Calculate payment delay for each client (oldest unpaid invoice)
    const getClientPaymentDelay = (clientId, clientName) => {
        const clientUnpaidInvoices = invoices.filter(inv =>
            (inv.clientId === clientId || inv.client_id === clientId ||
                inv.practitionerName === clientName || inv.practitioner_name === clientName) &&
            inv.status !== 'Payée'
        );

        if (clientUnpaidInvoices.length === 0) return { days: 0, hasAlert: false };

        // Find the oldest unpaid invoice
        const oldestDate = clientUnpaidInvoices.reduce((oldest, inv) => {
            const invDate = new Date(inv.date || inv.createdAt);
            return invDate < oldest ? invDate : oldest;
        }, new Date());

        const today = new Date();
        const diffTime = today - oldestDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return {
            days: diffDays,
            hasAlert: diffDays > 30,
            isWarning: diffDays > 15 && diffDays <= 30,
            isCritical: diffDays > 60
        };
    };

    // Compute paid status dynamically for all invoices of a client using FIFO
    const getInvoiceStatus = (invoice) => {
        if (invoice.status === 'Payée') return 'Payée';

        const clientId = invoice.clientId || invoice.client_id;
        const clientName = (invoice.practitionerName || invoice.practitioner_name || invoice.clientName || '').trim();
        const normName = clientName.toLowerCase();
        if (!clientId && !normName) return invoice.status || 'En attente';

        const matchesClient = (item) => {
            if (clientId && (item.clientId === clientId || item.client_id === clientId)) return true;
            if (!normName) return false;
            return (
                (item.practitionerName || '').trim().toLowerCase() === normName ||
                (item.practitioner_name || '').trim().toLowerCase() === normName ||
                (item.clientName || '').trim().toLowerCase() === normName
            );
        };

        // 1. Get all invoices of this client, sorted chronologically (oldest first)
        const clientInvoices = invoices
            .filter(matchesClient)
            .sort((a, b) => new Date(a.date || a.createdAt || a.created_at) - new Date(b.date || b.createdAt || b.created_at));

        // 2. Calculate client's total payments and approved credit notes
        const clientPayments = payments.filter(matchesClient);
        const clientCreditNotes = creditNotes.filter(n => matchesClient(n) && n.status !== 'pending');

        const totalPayments = clientPayments.reduce((sum, p) => {
            const amt = parseFloat(p.amount || 0);
            const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
            return sum + (isRefund ? -amt : amt);
        }, 0);
        const totalCredits = clientCreditNotes.reduce((sum, n) => sum + parseFloat(n.amount || 0), 0);

        let availableFunds = totalPayments + totalCredits;

        // 3. Allocate funds to invoices in chronological order (FIFO)
        for (const currentInv of clientInvoices) {
            const amount = parseFloat(currentInv.amount || currentInv.total_amount || currentInv.total || 0);
            if (currentInv.id === invoice.id) {
                if (availableFunds >= amount - 0.05) {
                    return 'Payée';
                } else if (availableFunds > 0.05) {
                    return 'Partielle';
                } else {
                    return 'En attente';
                }
            }
            availableFunds -= amount;
        }

        return invoice.status || 'En attente';
    };

    // Filter Logic for Invoices
    const filteredInvoices = invoices.filter(inv => {
        const status = getInvoiceStatus(inv);
        const matchesFilter = filter === 'pending' ? status !== 'Payée' :
            filter === 'paid' ? status === 'Payée' : true;

        const term = searchTerm.toLowerCase();
        const matchesSearch = 
            (inv.invoiceNumber && inv.invoiceNumber.toLowerCase().includes(term)) ||
            (inv.invoice_number && inv.invoice_number.toLowerCase().includes(term)) ||
            (inv.caseNumber && inv.caseNumber.toLowerCase().includes(term)) ||
            (inv.id && inv.id.toString().toLowerCase().includes(term)) ||
            (inv.practitionerName && inv.practitionerName.toLowerCase().includes(term)) ||
            (inv.practitioner_name && inv.practitioner_name.toLowerCase().includes(term)) ||
            (inv.patientName && inv.patientName.toLowerCase().includes(term)) ||
            (inv.patient_name && inv.patient_name.toLowerCase().includes(term));

        return matchesFilter && matchesSearch;
    }).sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));

    // Pagination calculations
    const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, filteredInvoices.length);
    const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

    const getPageNumbers = (current, total) => {
        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }
        if (current <= 4) {
            return [1, 2, 3, 4, 5, '...', total];
        }
        if (current >= total - 3) {
            return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        }
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    // Billable Orders Logic
    const billableOrders = orders.filter(
        o => {
            const s = o.status ? o.status.toLowerCase() : '';
            return (s === 'terminé' || s === 'livré') && s !== 'facturé' && !o.invoiceId;
        }
    );

    const togglePaymentStatus = async (invoice) => {
        const currentStatus = getInvoiceStatus(invoice);
        const newStatus = currentStatus === 'Payée' ? 'Emise' : 'Payée';
        const invLabel = invoice.invoiceNumber || invoice.invoice_number || (invoice.id || '').toString().substr(0, 8);
        const paymentAmount = parseFloat(invoice.amount || invoice.total_amount || 0);

        if (newStatus === 'Payée') {
            // Ask whether to only mark the invoice status as 'Payée' (e.g., already included in a global payment) or also record a new payment
            const shouldCreatePayment = window.confirm(
                `Facture ${invLabel} (${paymentAmount.toFixed(2)} €) :\n\n` +
                `• Cliquez sur "OK" pour enregistrer un NOUVEAU règlement de ${paymentAmount.toFixed(2)} € sur le compte du praticien.\n` +
                `• Cliquez sur "Annuler" si cette facture fait déjà partie d'un virement global et que vous souhaitez uniquement afficher le badge "Payée" sans modifier le solde.`
            );
            if (shouldCreatePayment && paymentAmount > 0) {
                try {
                    await addPayment({
                        clientId: invoice.clientId || invoice.client_id,
                        invoiceId: invoice.id,
                        amount: paymentAmount,
                        date: new Date().toISOString(),
                        type: 'Validation facture',
                        reference: `Paiement facture #${invLabel}`,
                    });
                } catch (err) {
                    console.error("Error creating auto-payment:", err);
                }
            }
        } else {
            // Marking as unpaid: remove any auto-created payment specifically linked to this invoice
            const linkedPayments = payments.filter(p =>
                (p.invoice_id === invoice.id || p.invoiceId === invoice.id)
            );
            for (const p of linkedPayments) {
                try {
                    await deletePayment(p.id);
                } catch (err) {
                    console.error("Error deleting auto-payment:", err);
                }
            }
        }

        updateInvoice(invoice.id, { status: newStatus });
    };

    const handleDeleteInvoice = (id) => {
        if (window.confirm('Voulez-vous vraiment supprimer cette facture ? Les fiches de travail associées seront marquées comme non facturées.')) {
            deleteInvoice(id);
        }
    };

    return (
        <div className="accounting-page">
            <div className="page-header d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1">Tableau de Bord Comptable</h2>
                    <p className="text-muted small">Suivi des revenus, paiements et facturation en attente.</p>
                </div>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-primary shadow-sm px-4 fw-bold"
                        style={{ borderRadius: '12px' }}
                        onClick={() => setShowBillable(!showBillable)}
                    >
                        <FaPlus className="me-2" /> Nouvelle Facture
                        {billableOrders.length > 0 && <span className="badge bg-danger ms-2">{billableOrders.length}</span>}
                    </button>
                </div>
            </div>

            {/* Stats Row */}
            <div className="accounting-stats">
                <div
                    className={`acc-stat-card revenue clickable-revenue ${activeTab === 'analytics' ? 'active-revenue-tab' : ''}`}
                    onClick={() => setActiveTab('analytics')}
                    title="Cliquer pour afficher les statistiques et l'évolution détaillée du chiffre d'affaires"
                >
                    <div className="icon"><FaFileInvoiceDollar /></div>
                    <span className="click-hint"><FaChartLine className="me-1" /> Analyser ↗</span>
                    <span className="label">Chiffre d'Affaires</span>
                    <span className="value">{totalBilled.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="acc-stat-card pending">
                    <div className="icon"><FaHandHoldingUsd /></div>
                    <span className="label">Reste à Encaisser</span>
                    <span className="value">{totalPending.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="acc-stat-card collected">
                    <div className="icon"><FaWallet /></div>
                    <span className="label">Total Encaissé</span>
                    <span className="value">{totalPaid.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="acc-stat-card orders">
                    <div className="icon"><FaHistory /></div>
                    <span className="label">Travaux à Facturer</span>
                    <span className="value">{billableOrders.length}</span>
                </div>
            </div>

            {/* Pending Credit Notes Alert Banner */}
            {(() => {
                const pendingNotes = (creditNotes || []).filter(n => n.status === 'pending');
                if (pendingNotes.length === 0) return null;
                return (
                    <div className="pending-credits-banner mb-4 animate__animated animate__fadeInDown">
                        <div className="pending-credits-header">
                            <div className="d-flex align-items-center gap-2">
                                <FaBell className="bell-icon" />
                                <h6 className="mb-0 fw-bold">Demandes d'avoir en attente ({pendingNotes.length})</h6>
                            </div>
                            <span className="text-muted small">Action requise</span>
                        </div>
                        <div className="pending-credits-list">
                            {pendingNotes.map(note => {
                                const client = clients.find(c => c.id === note.clientId || c.id === note.client_id);
                                const clientName = client?.name || note.clientName || 'Client inconnu';
                                return (
                                    <div key={note.id} className="pending-credit-item">
                                        <div className="pending-credit-info">
                                            <span className="pending-credit-client">{clientName}</span>
                                            <span className="pending-credit-amount">{parseFloat(note.amount || 0).toFixed(2)} €</span>
                                            <span className="pending-credit-date">{new Date(note.date || note.createdAt).toLocaleDateString('fr-FR')}</span>
                                            <span className="pending-credit-ref">{note.reference || note.reason || '-'}</span>
                                        </div>
                                        <div className="pending-credit-actions">
                                            <button
                                                className="btn btn-sm btn-success fw-bold px-3"
                                                onClick={() => {
                                                    if (window.confirm(`Accepter la demande d'avoir de ${clientName} pour ${parseFloat(note.amount || 0).toFixed(2)} € ?`)) {
                                                        updateCreditNote(note.id, { status: 'approved' });
                                                    }
                                                }}
                                            >
                                                <FaCheck className="me-1" /> Valider
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger fw-bold px-3"
                                                onClick={() => {
                                                    if (window.confirm(`Refuser et supprimer la demande d'avoir de ${clientName} ?`)) {
                                                        deleteCreditNote(note.id);
                                                    }
                                                }}
                                            >
                                                <FaTimes className="me-1" /> Refuser
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {/* Tabs */}
            <div className="acc-tabs mb-4">
                <button
                    className={`acc-tab ${activeTab === 'invoices' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('invoices'); setSelectedClientForStatement(null); }}
                >
                    <FaFileInvoice className="me-2" /> Historique des Factures
                </button>
                <button
                    className={`acc-tab ${activeTab === 'statements' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('statements'); setSelectedClientForStatement(null); }}
                >
                    <FaUserMd className="me-2" /> État des Comptes
                </button>
                <button
                    className={`acc-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('analytics'); setSelectedClientForStatement(null); }}
                >
                    <FaChartLine className="me-2 text-primary" /> Statistiques & Évolution CA 📈
                </button>
            </div>

            <div className="accounting-content">
                {/* Billable Section (if shown) */}
                {showBillable && (
                    <div className="acc-card animate__animated animate__fadeInDown mb-4">
                        <div className="acc-card-header" style={{ background: '#f8fafc' }}>
                            <h5><FaPlus className="text-primary me-2" /> Travaux Prêts à la Facturation</h5>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowBillable(false)}>
                                Masquer
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="acc-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Patient</th>
                                        <th>Praticien</th>
                                        <th className="text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billableOrders.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-4 text-muted">Aucun travail en attente.</td></tr>
                                    ) : (
                                        billableOrders.map(order => (
                                            <tr key={order.id}>
                                                <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                                                <td className="fw-bold">{order.patientName}</td>
                                                <td>Dr. {order.practitionerName}</td>
                                                <td className="text-end">
                                                    <button
                                                        className="btn btn-sm btn-success px-3"
                                                        style={{ borderRadius: '8px' }}
                                                        onClick={() => navigate(`/invoices/new/${order.id}`)}
                                                    >
                                                        Facturer <FaArrowRight className="ms-1 small" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'analytics' ? (
                    <RevenueAnalytics
                        invoices={invoices}
                        orders={orders}
                        clients={clients}
                        products={products}
                    />
                ) : activeTab === 'invoices' ? (
                    <div className="acc-card animate__animated animate__fadeIn">
                        <div className="acc-card-header">
                            <h5><FaHistory className="text-primary me-2" /> Historique des Factures</h5>
                        </div>

                        <div className="filter-bar">
                            <div className="search-box">
                                <FaSearch className="icon" />
                                <input
                                    type="text"
                                    placeholder="Rechercher une facture, un praticien ou un patient..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="filter-group">
                                <FaFilter size={12} className="me-2 text-muted" />
                                <select
                                    className="filter-select"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                >
                                    <option value="all">Toutes les factures</option>
                                    <option value="pending">En attente de paiement</option>
                                    <option value="paid">Payées</option>
                                </select>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="acc-table">
                                <thead>
                                    <tr>
                                        <th>N° Facture</th>
                                        <th>Date</th>
                                        <th>Praticien</th>
                                        <th>Patient</th>
                                        <th className="text-end">Montant</th>
                                        <th className="text-center">Statut</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedInvoices.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5 text-muted">
                                                Aucune facture trouvée.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedInvoices.map(inv => {
                                            const status = getInvoiceStatus(inv);
                                            return (
                                                <tr key={inv.id}>
                                                    <td className="fw-bold text-primary">{inv.invoiceNumber || inv.invoice_number || inv.caseNumber || `#${inv.id.substr(0, 8)}`}</td>
                                                    <td>{new Date(inv.date || inv.createdAt || inv.created_at).toLocaleDateString()}</td>
                                                    <td>{inv.practitionerName || inv.practitioner_name || '-'}</td>
                                                    <td>{inv.patientName || inv.patient_name || '-'}</td>
                                                    <td className="text-end fw-bold">{Number(inv.amount || inv.total_amount || 0).toFixed(2)} €</td>
                                                    <td className="text-center">
                                                        <span
                                                            className={`status-badge ${status === 'Payée' ? 'paid' : status === 'Partielle' ? 'partial' : 'pending'}`}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            onClick={() => togglePaymentStatus(inv)}
                                                            title={`Cliquer pour marquer comme ${status === 'Payée' ? 'En attente' : 'Payée'}`}
                                                        >
                                                            {status === 'Payée' ? <FaCheck size={10} /> : status === 'Partielle' ? <FaCheck size={10} /> : <FaTimes size={10} />}
                                                            {status === 'Payée' ? 'Payée' : status === 'Partielle' ? 'Partielle' : 'En attente'}
                                                        </span>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <InvoiceGenerator
                                                                invoice={inv}
                                                                order={orders.find(o => o.invoiceId === inv.id)}
                                                                isExisting={true}
                                                                buttonLabel=""
                                                            />
                                                            <button
                                                                className="btn-action-icon delete"
                                                                onClick={() => handleDeleteInvoice(inv.id)}
                                                                title="Supprimer la facture"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Footer */}
                        <div className="acc-card-footer d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <span className="text-muted small">
                                    {filteredInvoices.length === 0 ? (
                                        'Aucune facture trouvée'
                                    ) : (
                                        <>
                                            Affichage de <strong className="text-dark">{startIndex + 1}</strong> à <strong className="text-dark">{endIndex}</strong> sur <strong className="text-primary">{filteredInvoices.length}</strong> facture{filteredInvoices.length > 1 ? 's' : ''}
                                        </>
                                    )}
                                </span>
                                <div className="d-inline-flex align-items-center gap-2">
                                    <label className="text-muted small m-0" htmlFor="invoices-per-page">Par page :</label>
                                    <select
                                        id="invoices-per-page"
                                        className="form-select form-select-sm"
                                        style={{ width: 'auto', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={999999}>Toutes</option>
                                    </select>
                                </div>
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination-controls d-flex align-items-center gap-1">
                                    <button
                                        className="btn btn-sm btn-outline-secondary pagination-btn"
                                        disabled={validCurrentPage === 1}
                                        onClick={() => {
                                            setCurrentPage(prev => Math.max(1, prev - 1));
                                        }}
                                    >
                                        &laquo; Précédent
                                    </button>

                                    {getPageNumbers(validCurrentPage, totalPages).map((p, idx) => {
                                        if (p === '...') {
                                            return <span key={`ellipsis-${idx}`} className="px-2 text-muted fw-bold">...</span>;
                                        }
                                        return (
                                            <button
                                                key={p}
                                                className={`btn btn-sm ${validCurrentPage === p ? 'btn-primary fw-bold text-white shadow-sm' : 'btn-outline-secondary'} pagination-btn`}
                                                style={{ minWidth: '34px', borderRadius: '6px', fontSize: '13px' }}
                                                onClick={() => {
                                                    setCurrentPage(p);
                                                }}
                                            >
                                                {p}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="btn btn-sm btn-outline-secondary pagination-btn"
                                        disabled={validCurrentPage === totalPages}
                                        onClick={() => {
                                            setCurrentPage(prev => Math.min(totalPages, prev + 1));
                                        }}
                                    >
                                        Suivant &raquo;
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="acc-card animate__animated animate__fadeIn">
                        {selectedClientForStatement ? (
                            <div className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedClientForStatement(null)}>
                                        <FaArrowLeft className="me-2" /> Retour à la liste des praticiens
                                    </button>
                                    <h4 className="mb-0">Détails du compte : {selectedClientForStatement.name}</h4>
                                </div>
                                <AccountStatement clientId={selectedClientForStatement.id} clientName={selectedClientForStatement.name} />
                            </div>
                        ) : (
                            <>
                                <div className="acc-card-header">
                                    <h5><FaUserMd className="text-primary me-2" /> État des Comptes par Praticien</h5>
                                </div>
                                <div className="table-responsive">
                                    <table className="acc-table">
                                        <thead>
                                            <tr>
                                                <th>Praticien</th>
                                                <th className="text-end">Total Facturé</th>
                                                <th className="text-end">Total Réglé</th>
                                                <th className="text-end">Avoirs</th>
                                                <th className="text-end">Solde (Dette)</th>
                                                <th className="text-center">Retard</th>
                                                <th className="text-center">Dernier Règlement</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clients.map(client => {
                                                const summary = getClientAccountingSummary(client.id, client.name);
                                                const delay = getClientPaymentDelay(client.id, client.name);
                                                return (
                                                    <tr key={client.id}>
                                                        <td className="fw-bold">{client.name}</td>
                                                        <td className="text-end">{summary.totalBilled.toFixed(2)} €</td>
                                                        <td className="text-end text-success">{summary.totalPaid.toFixed(2)} €</td>
                                                        <td className="text-end text-primary">{summary.totalCredits.toFixed(2)} €</td>
                                                        <td className="text-end">
                                                            <span className={`fw-bold ${summary.balance > 0.01 ? 'text-danger' : 'text-success'}`}>
                                                                {summary.balance.toFixed(2)} €
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            {delay.days > 0 ? (
                                                                <span className={`badge ${delay.isCritical ? 'bg-danger' : delay.hasAlert ? 'bg-warning text-dark' : delay.isWarning ? 'bg-info' : 'bg-light text-dark'}`}>
                                                                    {delay.days}j
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-success">OK</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center small text-muted">
                                                            {summary.lastPaymentDate ? new Date(summary.lastPaymentDate).toLocaleDateString() : '-'}
                                                        </td>
                                                        <td className="text-end">
                                                            <div className="d-flex justify-content-end gap-1">
                                                                <PeriodicInvoiceGenerator
                                                                    clientId={client.id}
                                                                    clientName={client.name}
                                                                    clientAddress={client.address}
                                                                    clientPhone={client.phone}
                                                                    clientEmail={client.email}
                                                                    buttonLabel="Facture Mensuelle"
                                                                />
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary fw-bold"
                                                                    onClick={() => setSelectedClientForStatement(client)}
                                                                >
                                                                    Détails / Paiement
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Accounting;
